"use client";

import { Grid, GridItem } from "@/components/Grid/Grid.component";
import { Link } from "@/components/Link/Link.component";
import { Typography } from "@/components/Typography/Typography.component";
import { useBreakpoints } from "@/hooks/useBreakpoints/useBreakpoints";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { AwardsPropsModel } from "./Awards.model";

const Awards = ({ title, awardsCollection: items }: AwardsPropsModel) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoints();

  const [rowNumber, setRowNumber] = useState(Math.round(items.length / 2));
  const [rows, setRows] = useState(`grid-rows-[repeat(4,minmax(0,1fr))]`);
  const [viewAll, setViewAll] = useState(false);
  const [showItems, setShowItems] = useState(items.filter(Boolean));

  // Remove all falsy values (null, undefined, 0, false, "", NaN):
  items = items.filter(Boolean);

  const LineComponent = (props: any) => (
    <hr
      className={cn(
        "my-12 w-full md:my-10",
        props.transparent ? "border-transparent" : "border-core-black",
      )}
    />
  );

  useEffect(() => {
    if (isMobile) {
      const showItems = items.length > 4 ? items.slice(0, 4) : items;
      setShowItems(showItems);
      setRowNumber(showItems.length);
    }
    if (viewAll) {
      setRows(`!grid-rows-[repeat(${items.length},minmax(0,1fr))]`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, viewAll]);

  useEffect(() => {
    if (viewAll) {
      setShowItems(items);
      setRowNumber(items.length);
    }
  }, [items, viewAll]);

  return (
    <div className="bg-core-white px-10 py-20 max-md:px-4 max-md:py-10">
      <Grid hasPadding={false}>
        <GridItem span={{ mobile: 4, tablet: 8, desktop: 12 }}>
          <div
            className={cn(
              `grid auto-cols-fr max-md:grid-cols-3 md:grid-flow-col`,
              `items-end overflow-hidden`,
              `${isMobile ? rows : `grid-rows-${rowNumber}`}`,
            )}
            ref={nodeRef}
          >
            <div className="flex h-full flex-col justify-between md:items-start">
              <Typography.LLABEL1 className="pr-16 max-md:max-w-17 md:pr-4">
                {title}
              </Typography.LLABEL1>
              {showItems.length > 1 && <LineComponent />}
            </div>

            {new Array(rowNumber - 1).fill(0).map((_number, index) => (
              <div key={index} className="flex max-md:hidden">
                <LineComponent
                  key={index}
                  transparent={index === rowNumber - 2}
                />
              </div>
            ))}

            {showItems.map((item, index) => (
              <>
                {index !== 0 && (
                  <div className="flex md:hidden">
                    {index !== showItems.length - 1 && <LineComponent />}
                  </div>
                )}
                <div
                  key={item.sys?.id || index}
                  className={cn(
                    `${isMobile && "max-md:col-span-2"}`,
                    `flex h-full flex-col justify-between`,
                  )}
                >
                  <div className="pr-16 md:w-[240px] md:pr-0">
                    <Typography.LLABEL1>{item.company}</Typography.LLABEL1>
                    <Typography.LLABEL2>{item.award}</Typography.LLABEL2>
                  </div>
                  <div className="w-full">
                    {(rowNumber === 1 || (index + 1) % rowNumber !== 0) && (
                      <LineComponent />
                    )}
                  </div>
                </div>
              </>
            ))}
          </div>
          {!viewAll && isMobile && showItems.length < items.length && (
            <Typography.LLABEL1 className="text-center">
              <Link as="re" href={""} onClick={() => setViewAll(true)}>
                VIEW ALL
              </Link>
            </Typography.LLABEL1>
          )}
        </GridItem>
      </Grid>
    </div>
  );
};

export default Awards;
